import { createSelector } from 'reselect'
import { convertFromCentsToDollarsString } from 'components/FormatPrice/helpers'

import {
  isAuthenticated,
  getFullName,
  getFirstAndLastName,
  getUserRole,
} from 'selectors/user/session'
import { getPageData, getPagePath } from 'selectors/page'
import {
  getCustomerId,
  getCustomerAddressBookItemByIdOrNull,
  getCustomerEmail,
} from 'selectors/customer'
import { getUserPreferencesSelectedCurrency } from 'selectors/user/cookie'
import { Cart, CartItem } from './types'
import { Address } from 'types/Customer'
import { GIFT_CARD } from 'components/Payment/helpers/constants'

const { SITE_URL } = process.env

const getState = (state: Record<string, any>) => state || {}

export const getSection = createSelector([getPageData], ({ section }): string => section || 'cart')

export const getCart = (state: Record<string, any>): Cart => state.cart || {}

export const getResetCart = createSelector(
  [getCart],
  ({ resetCart }): boolean => resetCart || false
)

export const getCartNotifications = createSelector(
  [getCart],
  ({ notifications }): Array<string | null | undefined> => notifications || []
)

export const getCartCouponCode = createSelector(
  [getCart],
  ({ couponCode }): string => couponCode || ''
)

export const getCartItems = createSelector([getCart], ({ items }): Array<CartItem> => items || [])

export const getCartItemsCount = createSelector(
  [getCart],
  ({ itemsCartCount }): number => itemsCartCount || 0
)

export const getCartSummary = createSelector(
  [getCart],
  ({
    giftCardBalance,
    giftCardAmountAppliedToCart,
    giftCardAmountAppliedToCheckout,
    isFinalSale,
    isFinalSaleOriginal,
    isFinalSalePrint,
    isFinalSaleAple,
    isFinalSaleStockIssue,
    subTotalCartPrice,
    subTotalCheckoutPrice,
    subTotalPrice,
    totalAgentFeeAmount,
    totalCrateFeeAmount,
    totalCustomsAndDutiesAmount,
    totalDiscountAmount,
    totalHandlingFeeAmount,
    totalOrderPriceToPay,
    totalOrderPriceToPayCart,
    totalOrderPriceToPayCheckout,
    totalShippingAgentFeeAmount,
    totalShippingPrice,
    totalTaxAmount,
    totalTaxPrice,
    totalVatAmount,
  }): Record<string, any> => ({
    giftCardBalance,
    giftCardAmountAppliedToCart,
    giftCardAmountAppliedToCheckout,
    isFinalSale,
    isFinalSaleOriginal,
    isFinalSalePrint,
    isFinalSaleAple,
    isFinalSaleStockIssue,
    subTotalCartPrice,
    subTotalCheckoutPrice,
    subTotalPrice,
    totalAgentFeeAmount,
    totalCrateFeeAmount,
    totalCustomsAndDutiesAmount,
    totalDiscountAmount,
    totalHandlingFeeAmount,
    totalOrderPriceToPay,
    totalOrderPriceToPayCart,
    totalOrderPriceToPayCheckout,
    totalShippingAgentFeeAmount,
    totalShippingPrice,
    totalTaxAmount,
    totalTaxPrice,
    totalVatAmount,
  })
)

export const getTotalOrderPriceToPayCheckout = createSelector(
  [getCartSummary],
  ({ totalOrderPriceToPayCheckout }): number => totalOrderPriceToPayCheckout || 0
)

export const getOrderNumber = createSelector(
  [getCart],
  ({ orderNumber }): string => orderNumber || ''
)

export const getCouponCode = createSelector([getCart], ({ couponCode }): string => couponCode || '')

export const getGiftCardBalance = createSelector([getCart], ({ giftCardBalance }): number =>
  Number(giftCardBalance || 0)
)

export const getGiftCardCode = createSelector(
  [getCart],
  ({ giftCardCode }): string => giftCardCode || ''
)

export const getSelectedShippingInfo = createSelector(
  [getCart],
  ({ selectedShippingInfo }) => selectedShippingInfo || {}
)

export const getAddressBookItemId = createSelector(
  [getSelectedShippingInfo],
  ({ addressBookItemId }): string => addressBookItemId || ''
)

export const getAddressGuest = createSelector([getSelectedShippingInfo], ({ guest }) => guest || '')

export const getAddressItem = createSelector(
  [getState, getAddressBookItemId],
  (state, addressBookItemId) =>
    createSelector(
      [getCustomerAddressBookItemByIdOrNull(addressBookItemId)],
      (address): Address | object => address || {}
    )(state)
)

export const getAddressItemOrNull = createSelector(
  [getState, getAddressBookItemId],
  (state, addressBookItemId) =>
    createSelector(
      [getCustomerAddressBookItemByIdOrNull(addressBookItemId)],
      (address): Address | null => address
    )(state)
)

export const getAddressItemCountryCode = createSelector(
  [getAddressItemOrNull],
  (address): string => address?.iso2Country ?? ''
)

export const getCustomerPayment = createSelector(
  [getCart],
  ({ customerPayment }): Record<string, any> => customerPayment || {}
)

export const getWalletPaymentMethodId = createSelector(
  [getCustomerPayment],
  ({ useWallet, paymentMethodId }): boolean => (useWallet ? paymentMethodId : '')
)

export const getGuestPaymentFormatted = createSelector([getCustomerPayment], (customerPayment) => {
  const {
    expiration,
    expressCheckoutType,
    last4Digits,
    paymentMethodId,
    paymentMethodType,
    type,
    confirmationToken,
  } = customerPayment

  return {
    ccLast4Digits: last4Digits,
    ccType: type,
    expiration,
    expressCheckoutType: expressCheckoutType || '',
    paymentMethodId,
    paymentMethodType,
    ...(confirmationToken && { confirmationToken }),
  }
})

export const getPaymentDetails = createSelector(
  [getGuestPaymentFormatted, getGiftCardCode, getTotalOrderPriceToPayCheckout],
  (customerPayment, giftCard, totalPayment): Record<string, any> => {
    if (totalPayment === 0 && !!giftCard) {
      return {
        paymentMethodId: giftCard,
        paymentMethodType: GIFT_CARD,
      }
    }
    return customerPayment
  }
)

export const getHasShippingAddress = createSelector(
  [getAddressBookItemId],
  (addressBookItemId): boolean => {
    return !!addressBookItemId || false
  }
)

export const getHasPaymentMethodType = createSelector(
  [getPaymentDetails],
  ({ paymentMethodType }): boolean => {
    return !!paymentMethodType || false
  }
)

export const getHasPaymentMethodId = createSelector(
  [getPaymentDetails],
  ({ paymentMethodId }): boolean => {
    return !!paymentMethodId || false
  }
)

export const getHasPayment = createSelector(
  [getHasPaymentMethodType, getHasPaymentMethodId],
  (paymentMethodType, paymentMethodId): boolean => {
    return (!!paymentMethodType && !!paymentMethodId) || false
  }
)

export const getIsCheckoutValid = createSelector(
  [getHasShippingAddress, getHasPaymentMethodType, getHasPaymentMethodId],
  (hasShippingAddress, hasPaymentMethodType, hasPaymentMethodId): boolean => {
    if (!hasShippingAddress) {
      return false
    }

    return !!hasPaymentMethodType && !!hasPaymentMethodId
  }
)

// We need to create the cart payload for place order, this is used for GUEST only
export const getPlaceOrderPayload = createSelector(
  [getCustomerEmail, getCustomerPayment, getAddressItemOrNull],
  (email, customerPayment, shippingAddress): Record<string, any> => {
    return {
      ...customerPayment,
      email: email || shippingAddress?.email,
    }
  }
)

export const getAffirmPayload = createSelector(
  [
    isAuthenticated,
    getCartItems,
    getCartSummary,
    getAddressItemOrNull,
    getCustomerId,
    getCustomerEmail,
    getFullName,
    getFirstAndLastName,
  ],
  (
    authenticated,
    cartItems,
    cartSummary,
    shippingAddress,
    customerId,
    customerEmail,
    fullName,
    firstLastData
  ): Record<string, any> => {
    const { totalOrderPriceToPay, totalShippingPrice, totalTaxAmount } = cartSummary
    const data = {
      baseUrl: SITE_URL || '',
      cartItems,
      shippingAddress,
      totalAmount: totalOrderPriceToPay,
      taxesAmount: totalTaxAmount,
      shippingAmount: totalShippingPrice,
    }

    if (authenticated) {
      const { firstName, lastName } = firstLastData
      return {
        ...data,
        customer: {
          customerId,
          email: customerEmail,
          firstName,
          fullName,
          lastName,
        },
      }
    }

    return {
      ...data,
      customer: {
        customerId,
        email: customerEmail,
        firstName: shippingAddress?.firstName,
        fullName: shippingAddress?.fullName,
        lastName: shippingAddress?.lastName,
      },
    }
  }
)

// Use the SKU to grab the PRODUCT info from CART.ITEMS
export const getProductModalInfo = (sku: string = '') =>
  createSelector(
    [getCartItems],
    (cartItems): Record<string, any> => {
      if (!sku) {
        return {}
      }

      return cartItems.find((item) => item.sku === sku) || {}
    }
  )

// Gather INFO needed for all GTM data - used in hook =>
export const getGTMData = createSelector(
  [
    getCart,
    getCustomerId,
    getCustomerEmail,
    getUserPreferencesSelectedCurrency,
    getPagePath,
    getIsCheckoutValid,
    getPaymentDetails,
    isAuthenticated,
    getUserRole,
  ],
  (
    cart,
    customerId,
    email,
    selectedCurrency,
    pagePath,
    isCheckoutValid,
    paymentDetails,
    authenticated,
    userRole
  ): Record<string, any> => {
    const { currencyCode } = selectedCurrency
    return {
      authenticated,
      cart,
      currencyCode,
      customerId,
      email,
      isCheckoutValid,
      pagePath,
      paymentDetails,
      userRole,
    }
  }
)

// GA4 data layer event =>
export const getGA4Data = createSelector(
  [getCart, getCouponCode, getCartItems],
  (cart, couponCode, items) => {
    const { totalOrderPriceToPayCart, totalDiscountAmount } = cart
    const cartItems = items.map((item, index) => {
      const {
        artistFirstName,
        artistLastName,
        artworkId,
        price,
        productCategory,
        productName,
        productType,
        sku,
      } = item
      const { discountPerUnit, subTotalPricePerUnit, quantity } = price
      return {
        affiliation: 'Online Store',
        coupon: couponCode,
        discount: convertFromCentsToDollarsString(discountPerUnit),
        index, // The index/position of the item in a list.
        item_art_id: artworkId, // artwork id
        item_brand: `${artistFirstName || ''} ${artistLastName || ''}`, // artist name
        item_category: productCategory, // category
        item_id: sku, // SKU
        item_name: productName, // artwork title
        item_variant: productType === 'Original Work' ? 'original' : 'open',
        price: convertFromCentsToDollarsString(subTotalPricePerUnit), // price before discount
        ...(discountPerUnit !== 0 && {
          discounted_price:
            parseFloat(convertFromCentsToDollarsString(subTotalPricePerUnit)) -
            parseFloat(convertFromCentsToDollarsString(discountPerUnit)),
        }), // price AFTER discount
        quantity,
      }
    })
    return {
      ecommerce: {
        coupon: couponCode || '',
        currency: 'USD',
        items: cartItems || [],
        value: convertFromCentsToDollarsString(totalOrderPriceToPayCart - totalDiscountAmount),
      },
    }
  }
)

// Custom update cart data layer event =>
export const getUpdateCartEventData = createSelector(
  [getCart, getCouponCode, getCartItems],
  (cart, couponCode, items) => {
    const { totalOrderPriceToPayCart, totalDiscountAmount, itemsCartCount } = cart
    const cartItems = items.map((item, index) => {
      const {
        sku,
        productCategory,
        productName,
        price,
        artistFirstName,
        artistLastName,
        productType,
        artworkId,
      } = item
      const { discountPerUnit, subTotalPricePerUnit, quantity } = price
      return {
        item_sku: sku, // SKU
        item_art_id: artworkId, // artwork id
        item_title: productName, // artwork title
        coupon: couponCode,
        discount: convertFromCentsToDollarsString(discountPerUnit),
        artist_name: `${artistFirstName || ''} ${artistLastName || ''}`, // artist name
        item_category: productCategory, // category
        item_variant: productType === 'Original Work' ? 'original' : 'open',
        price: convertFromCentsToDollarsString(subTotalPricePerUnit), // price before discount
        ...(discountPerUnit !== 0 && {
          discounted_price:
            parseFloat(convertFromCentsToDollarsString(subTotalPricePerUnit)) -
            parseFloat(convertFromCentsToDollarsString(discountPerUnit)),
        }), // price AFTER discount
        quantity,
      }
    })
    return {
      cart: {
        subtotal: convertFromCentsToDollarsString(totalOrderPriceToPayCart - totalDiscountAmount),
        coupon: couponCode || '',
        quantity: itemsCartCount || 0,
        cart_items: cartItems || [],
      },
    }
  }
)
